import { useMemo } from 'react';
import { Avatar, Card, DatePicker, Select, Space, Table, Tabs, Tag, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';

import { useMessagesQuery, type Channel, type Message, type MessageStatus } from './hooks';
import { MessageStatusTag } from './MessageStatusTag';
import { ChannelTag } from './ChannelTag';
import { errorCodeText } from './errorCodeText';
import { ErrorAlert } from '@/components/ErrorAlert';
import { formatDateTime } from '@/lib/format';
import { useCustomersQuery, type Customer } from '@/customers/hooks';

// Cap для упрощённой реализации: в MVP подгружаем до 100 живых клиентов
// для рендера селектора и колонки «Клиент». Для большой оптики (>100)
// понадобится server-side search endpoint — backlog.
const CUSTOMERS_CAP = 100;

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('');
}

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// «Агрегации» табов журнала. На бэк уходит **один** конкретный MessageStatus
// (схема API на M2 не принимает массив); поэтому для каждой агрегации мы
// передаём первичный статус. design просит «Отправлены» = sent+delivered, но
// на M2 Telegram до `delivered` не доходит — выбираем `sent` как primary.
type StatusTabKey = 'all' | 'sent' | 'queued' | 'failed' | 'skipped';

const STATUS_TABS: Record<StatusTabKey, MessageStatus | undefined> = {
  all: undefined,
  sent: 'sent',
  queued: 'queued',
  failed: 'failed',
  skipped: 'skipped',
};

function parseStatusTab(raw: string | null): StatusTabKey {
  if (raw && Object.prototype.hasOwnProperty.call(STATUS_TABS, raw)) {
    return raw as StatusTabKey;
  }
  return 'all';
}

function parseChannel(raw: string | null): Channel | undefined {
  if (raw === 'telegram' || raw === 'sms' || raw === 'whatsapp' || raw === 'push') {
    return raw;
  }
  return undefined;
}

export function MessagesPage(): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const page = Number(searchParams.get('page') ?? '1') || 1;
  const pageSize = Number(searchParams.get('page_size') ?? '20') || 20;
  const statusTab = parseStatusTab(searchParams.get('tab'));
  const channel = parseChannel(searchParams.get('channel'));
  const customerId = searchParams.get('customer_id') ?? undefined;
  const fromIso = searchParams.get('from') ?? undefined;
  const toIso = searchParams.get('to') ?? undefined;

  const status = STATUS_TABS[statusTab];

  // Список живых клиентов для селектора и резолва имён в колонке.
  // Soft-deleted клиенты на сервере отфильтрованы (M2.2 D8): если customer_id
  // сообщения не нашёлся в Map — считаем что клиент удалён, показываем «удалён».
  const customersQuery = useCustomersQuery({ page: 1, pageSize: CUSTOMERS_CAP });
  const customersById = useMemo(() => {
    const map = new Map<string, Customer>();
    for (const c of customersQuery.data?.items ?? []) {
      map.set(c.id, c);
    }
    return map;
  }, [customersQuery.data]);

  const queryParams = useMemo(
    () => ({
      page,
      pageSize,
      ...(status ? { status } : {}),
      ...(channel ? { channel } : {}),
      ...(customerId ? { customerId } : {}),
      ...(fromIso ? { from: fromIso } : {}),
      ...(toIso ? { to: toIso } : {}),
    }),
    [page, pageSize, status, channel, customerId, fromIso, toIso],
  );

  const { data, isLoading, error } = useMessagesQuery(queryParams);

  const setParam = (name: string, value: string | undefined): void => {
    const sp = new URLSearchParams(searchParams);
    if (value === undefined || value === '') sp.delete(name);
    else sp.set(name, value);
    sp.set('page', '1');
    setSearchParams(sp);
  };

  const onTabChange = (key: string): void => setParam('tab', key === 'all' ? undefined : key);
  const onChannelChange = (val: Channel | undefined): void => setParam('channel', val);

  const onDateRangeChange = (range: [Dayjs | null, Dayjs | null] | null): void => {
    const sp = new URLSearchParams(searchParams);
    if (!range) {
      sp.delete('from');
      sp.delete('to');
    } else {
      const [from, to] = range;
      if (from) sp.set('from', from.toISOString());
      else sp.delete('from');
      if (to) sp.set('to', to.toISOString());
      else sp.delete('to');
    }
    sp.set('page', '1');
    setSearchParams(sp);
  };

  const columns: ColumnsType<Message> = [
    {
      title: t('messages.table.time'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 160,
      render: (v: string) => (
        <Text type="secondary" className="sl-mono" style={{ fontSize: 12 }}>
          {formatDateTime(v)}
        </Text>
      ),
    },
    {
      title: t('messages.table.customer'),
      key: 'customer',
      width: 240,
      render: (_v, m) => {
        const cust = customersById.get(m.customer_id);
        if (cust) {
          return (
            <Space
              size={8}
              data-testid={`message-customer-${m.id}`}
              style={{ cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/customers/${cust.id}`);
              }}
            >
              <Avatar size="small">{initials(cust.name) || '?'}</Avatar>
              <Text>{cust.name}</Text>
            </Space>
          );
        }
        // Клиент не нашёлся в списке живых → soft-deleted (или вне CAP-окна).
        // Показываем placeholder бледно-серым + tag «удалён».
        return (
          <Space size={8} data-testid={`message-customer-${m.id}-deleted`}>
            <Avatar size="small" style={{ background: 'var(--ant-color-text-disabled, #bfbfbf)' }}>
              ?
            </Avatar>
            <Text type="secondary" italic>
              {t('customer.deleted_placeholder')}
            </Text>
            <Tag>{t('customer.deleted_badge')}</Tag>
          </Space>
        );
      },
    },
    {
      title: t('messages.table.channel'),
      dataIndex: 'channel',
      key: 'channel',
      width: 110,
      render: (c: Channel) => <ChannelTag channel={c} />,
    },
    {
      title: t('messages.table.body'),
      dataIndex: 'body',
      key: 'body',
      ellipsis: true,
      render: (b: string) => <Text>{b}</Text>,
    },
    {
      title: t('messages.table.status'),
      key: 'status',
      width: 130,
      render: (_v, m) => <MessageStatusTag status={m.status} errorCode={m.error_code} />,
    },
    {
      title: t('messages.table.error_code'),
      dataIndex: 'error_code',
      key: 'error_code',
      width: 220,
      render: (code: string | null) => {
        if (!code) return <Text type="secondary">—</Text>;
        const human = errorCodeText(t, code);
        // Если перевода нет — `human === code`, рендерим только моноширинный raw.
        // Если перевод есть — основной текст человекочитаемый, под ним мелким моно — raw code.
        if (human === code) {
          return (
            <Text type="danger" className="sl-mono" style={{ fontSize: 12 }}>
              {code}
            </Text>
          );
        }
        return (
          <Space direction="vertical" size={0}>
            <Text type="danger" style={{ fontSize: 12 }}>
              {human}
            </Text>
            <Text type="secondary" className="sl-mono" style={{ fontSize: 11 }}>
              {code}
            </Text>
          </Space>
        );
      },
    },
  ];

  const total = data?.total ?? 0;
  const items = data?.items ?? [];

  const tabItems = (Object.keys(STATUS_TABS) as StatusTabKey[]).map((key) => ({
    key,
    label: t(`messages.tabs.${key}`),
  }));

  return (
    <div>
      <Title level={3} style={{ marginBottom: 4 }}>
        {t('messages.title')}
      </Title>
      <Text type="secondary">{t('messages.subtitle')}</Text>

      <Card style={{ marginTop: 16, marginBottom: 16 }} variant="outlined">
        <Tabs activeKey={statusTab} onChange={onTabChange} items={tabItems} />
        <Space size="middle" wrap style={{ marginTop: 12 }}>
          <Select<Channel | undefined>
            allowClear
            placeholder={t('messages.filters.channel_all')}
            value={channel}
            onChange={(v) => onChannelChange(v)}
            style={{ minWidth: 180 }}
            options={[
              { value: 'telegram', label: 'Telegram' },
              { value: 'sms', label: 'SMS' },
            ]}
          />
          <Select<string | undefined>
            allowClear
            showSearch
            placeholder={t('messages.filters.customer_placeholder')}
            value={customerId}
            onChange={(v) => setParam('customer_id', v)}
            style={{ minWidth: 240 }}
            loading={customersQuery.isLoading}
            optionFilterProp="label"
            filterOption={(input, option) =>
              (option?.label ?? '').toString().toLowerCase().includes(input.toLowerCase())
            }
            options={(customersQuery.data?.items ?? []).map((c) => ({
              value: c.id,
              label: c.name,
            }))}
            notFoundContent={t('messages.filters.customer_search_placeholder')}
          />
          <RangePicker
            showTime={{ format: 'HH:mm' }}
            format="DD.MM.YYYY HH:mm"
            value={
              fromIso || toIso
                ? [fromIso ? dayjs(fromIso) : null, toIso ? dayjs(toIso) : null]
                : null
            }
            onChange={(range) => onDateRangeChange(range as [Dayjs | null, Dayjs | null] | null)}
          />
          {customerId && (
            <Tag closable onClose={() => setParam('customer_id', undefined)}>
              {t('messages.filters.customer_active', { id: customerId.slice(0, 8) })}
            </Tag>
          )}
        </Space>
      </Card>

      {error && <ErrorAlert error={error} />}

      <Table<Message>
        rowKey="id"
        loading={isLoading}
        dataSource={items}
        columns={columns}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: false,
          onChange: (p, ps) => {
            const sp = new URLSearchParams(searchParams);
            sp.set('page', String(p));
            sp.set('page_size', String(ps));
            setSearchParams(sp);
          },
        }}
        locale={{
          emptyText: (
            <div style={{ padding: '32px 0', textAlign: 'center' }}>
              <Text strong style={{ display: 'block', marginBottom: 4 }}>
                {t('messages.empty.title')}
              </Text>
              <Text type="secondary">{t('messages.empty.subtitle')}</Text>
            </div>
          ),
        }}
      />
    </div>
  );
}
